import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/* ============================================================
   CAPEX Design System — « Graphite & Orange »
   Composants partagés (CAPEX_DA_Emergent.md §4). Visuel only.
   ============================================================ */

// §4.3 En-tête de page : sur-titre + H1 majuscules + actions à droite
export const PageHeader = ({ overline, title, children, className }) => (
  <div className={cn('flex flex-wrap items-end justify-between gap-4 mb-6', className)} data-testid="page-header">
    <div className="min-w-0">
      {overline && (
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-1">{overline}</p>
      )}
      <h1 className="font-display font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink text-4xl sm:text-5xl">{title}</h1>
    </div>
    {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
  </div>
);

// §4.5 Sélecteur segmenté
export const Segmented = ({ options = [], value, onChange, className, size = 'md' }) => (
  <div className={cn('inline-flex items-center gap-0.5 rounded-ctrl bg-segmented p-1', className)} data-testid="segmented">
    {options.map((opt) => {
      const v = typeof opt === 'string' ? opt : opt.value;
      const label = typeof opt === 'string' ? opt : opt.label;
      const active = v === value;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange && onChange(v)}
          className={cn(
            'rounded-[7px] font-semibold transition-colors whitespace-nowrap',
            size === 'sm' ? 'h-8 px-3 text-xs' : 'h-9 px-4 text-[13px]',
            active ? 'bg-graphite-900 text-white' : 'text-text-2 hover:text-ink'
          )}
          data-testid={`segmented-option-${v}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

// §4.7 Carte simple
export const Card = ({ className, children, ...props }) => (
  <div className={cn('rounded-card bg-surface p-5 sm:p-6', className)} {...props}>{children}</div>
);

// §4.7 Bandeau scène graphite (une seule « scène » par écran)
export const SceneBand = ({ className, children, ...props }) => (
  <div className={cn('rounded-scene bg-graphite-900 p-6 sm:p-7', className)} data-testid="scene-band" {...props}>
    {children}
  </div>
);

// Indicateur chiffré dans un bandeau scène
export const SceneStat = ({ label, value, caption, valueClassName, first, children }) => (
  <div className={cn('px-5 first:pl-0', !first && 'border-l border-graphite-600')}>
    {label && <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">{label}</p>}
    <p className={cn('font-display font-semibold text-white text-[40px] leading-none tabular-nums', valueClassName)}>{value}</p>
    {caption && <p className="text-[13px] text-ondark-2 mt-1.5">{caption}</p>}
    {children}
  </div>
);

// §4.6 Tuile module cliquable
export const ModuleTile = ({ to, icon: Icon, number, badge, family, title, value, caption }) => (
  <Link
    to={to}
    className="group relative flex flex-col justify-between rounded-scene bg-surface p-5 min-h-[196px] transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[0_0_0_2px_#25272B,0_12px_24px_rgba(37,39,43,0.12)]"
    data-testid={`module-tile-${to.replace(/^\//, '')}`}
  >
    <div className="flex items-start justify-between">
      <div className="w-[46px] h-[46px] rounded-ctrl bg-graphite-900 flex items-center justify-center">
        {Icon && <Icon className="w-[22px] h-[22px] text-orange" strokeWidth={1.9} />}
      </div>
      {badge ? (
        <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-orange text-orange-on text-[13px] font-bold">{badge}</span>
      ) : (
        number != null && <span className="font-display text-lg text-[#C9C6BE]">{number}</span>
      )}
    </div>
    <div className="mt-4">
      {family && <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-label mb-0.5">{family}</p>}
      <h3 className="font-display font-bold uppercase text-[23px] leading-tight text-ink">{title}</h3>
    </div>
    <div className="flex items-end justify-between mt-3">
      <div className="min-w-0">
        {value != null && <p className="font-display font-semibold text-[32px] leading-none tabular-nums text-ink">{value}</p>}
        {caption && <p className="text-[13px] text-text-3 mt-1 truncate">{caption}</p>}
      </div>
      <span className="shrink-0 w-9 h-9 rounded-full bg-bg flex items-center justify-center transition-colors group-hover:bg-orange">
        <ArrowRight className="w-4 h-4 text-graphite-900 group-hover:text-orange-on" strokeWidth={2.2} />
      </span>
    </div>
  </Link>
);

// §4.9 Pastilles de statut (mapping unique)
const PILL_ORANGE = ['à traiter', "en cours d'étude", 'en cours d etude', 'go validé', 'go valide', 'nouveau', 'en attente', 'à qualifier', 'a qualifier'];
const PILL_DARK = ['rdv pris', 'gagné', 'gagne', 'signé', 'signe', 'signés', 'signes', 'validé', 'valide', 'envoyé', 'envoye', 'validé / envoyé'];
export const StatusPill = ({ status, outline = false, className }) => {
  const key = String(status || '').trim().toLowerCase();
  let style = 'bg-surface-2 text-[#2E2D2A]';
  if (PILL_ORANGE.some(s => key === s || key.includes(s))) style = 'bg-orange-tint text-orange-tint-text';
  else if (PILL_DARK.some(s => key === s || key.includes(s))) style = 'bg-graphite-900 text-white';
  if (outline) style = 'bg-surface border border-border text-text-2';
  return (
    <span className={cn('inline-flex items-center px-2.5 py-1 rounded-[6px] text-xs font-bold', style, className)} data-testid="status-pill">
      {status}
    </span>
  );
};

// §4.8 Conteneur de tableau
export const TableShell = ({ className, children }) => (
  <div className={cn('rounded-card bg-surface overflow-x-auto', className)}>
    {children}
  </div>
);
